/**
 * Logo Processor Utility
 * Processes logo images to adjust colors based on background
 */

/**
 * Process logo for background contrast
 * @param {string} logoUrl - URL of the logo image
 * @param {'white' | 'black' | 'color'} mode - Color mode to apply
 * @returns {Promise<string>} Data URI of processed logo
 */
export async function processLogoForBackground(logoUrl, mode) {
  // If keeping original color, return as-is
  if (mode === "color") {
    return logoUrl;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Convert to monochrome while preserving alpha
        const targetColor = mode === "white" ? 255 : 0;

        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];

          // Only modify non-transparent pixels
          if (alpha > 0) {
            data[i] = targetColor; // R
            data[i + 1] = targetColor; // G
            data[i + 2] = targetColor; // B
            // Keep alpha as-is: data[i + 3]
          }
        }

        ctx.putImageData(imageData, 0, 0);
        const dataUri = canvas.toDataURL("image/png");

        resolve(dataUri);
      } catch (error) {
        console.error("Error processing logo:", error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error("Failed to load logo for processing:", error);
      reject(new Error("Failed to load logo for processing"));
    };

    img.src = logoUrl;
  });
}

/**
 * Validate if logo URL is processable
 * @param {string} logoUrl - URL to validate
 * @returns {boolean}
 */
export function canProcessLogo(logoUrl) {
  if (!logoUrl || typeof logoUrl !== "string") {
    return false;
  }

  // Can't process CloudFront images that might have CORS issues
  if (logoUrl.includes("cloudfront.net")) {
    return false;
  }

  // Check if it's a valid image URL
  const imageExtensions = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".gif"];
  const hasImageExtension = imageExtensions.some((ext) =>
    logoUrl.toLowerCase().includes(ext)
  );

  return hasImageExtension || logoUrl.startsWith("data:image");
}

/**
 * Apply color filter to logo (alternative method using CSS filters)
 * @param {'white' | 'black' | 'color'} mode - Color mode
 * @returns {string} CSS filter string
 */
export function getLogoFilterCSS(mode) {
  if (mode === "color") {
    return "";
  }

  if (mode === "white") {
    // Convert to white: increase brightness and reduce saturation
    return "brightness(0) invert(1)";
  }

  if (mode === "black") {
    // Convert to black: reduce brightness
    return "brightness(0)";
  }

  return "";
}
